<template>
    <div class="tile">
        <div class="tile-title">
            <h2>Downtime Schedule</h2>
        </div>
        <div class="tile-content">
            <p class="text-muted stack-m">
                Sometimes payment gateways have scheduled downtime or maintenance windows. You can note these here, and Rebilly will divert your incoming transactions to any of your other available and valid gateways. Rebilly will also pause any rebills scheduled for the payment gateway until the scheduled downtime window is over.
            </p>
            <ul class="list list-group list-divided stack-m">
                <li class="list-item align-items-center" v-for="gatewayAccountDowntime in gatewayAccountDowntimes">
                    <span class="list-item-content-primary">
                        {{gatewayAccountDowntime.startTime | dateTime}} - {{gatewayAccountDowntime.endTime | dateTime}}
                    </span>
                    <div class="list-item-content inline-l">
                        <span :class="getBadgeType(gatewayAccountDowntime)">{{gatewayAccountDowntime.status | capitalize}}</span>
                    </div>
                    <div class="list-item-content no-flex">
                        <gateway-downtime-form-modal trigger-size="small" class="inline-s" :value="gatewayAccountDowntime" @submit="updateGatewayAccountDowntime"/><v-icon-button type="basic" size="small" @click="showDeleteDialog(gatewayAccountDowntime)"><v-icon icon="trash"/></v-icon-button>
                    </div>
                </li>
            </ul>
            <gateway-downtime-form-modal
                trigger-title="Add New Schedule"
                modal-title="Add New Schedule"
                trigger-type="link"
                @submit="saveGatewayAccountDowntime"/>
        </div>
    </div>
</template>

<script>
    import {vIcon, vIconButton} from '@/components/form';
    import GatewayAccountDowntimeModel from '@/common/models/gateway-account-downtime-model';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import detailsMixins from '../details-mixins';
    import GatewayDowntimeFormModal from './gateway-downtime-form-modal';


    export default {
        name: 'gateway-downtimes',
        mixins: [detailsMixins, LoadStateMixin],
        components: {
            GatewayDowntimeFormModal,
            vIconButton,
            vIcon,
        },
        data() {
            return {
                gatewayAccountDowntimes: null,
            };
        },
        methods: {
            getBadgeType({status}) {
                const badgeTypeStatusMap = {
                    pending: 'positive',
                    ongoing: 'warning',
                };
                const badgeType = badgeTypeStatusMap[status] || badgeTypeStatusMap.ongoing;
                return `badge badge-${badgeType}`;
            },
            async showDeleteDialog(gatewayAccountDowntime) {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Delete downtime schedule',
                    content: 'Are you sure you want to delete this downtime schedule?',
                    confirmLabel: 'Yes',
                    cancelLabel: 'No',
                };
                if (await confirmDialog(dialogParams)) {
                    this.deleteGatewayAccountDowntime(gatewayAccountDowntime);
                }
            },
            async deleteGatewayAccountDowntime({id}) {
                const params = {
                    id: this.gateway.id,
                    downtimeScheduleId: id,
                };
                try {
                    await this.$api(({gatewayAccounts}) => gatewayAccounts.deleteDowntimeSchedule(params));
                    await this.notifyGatewayUpdate(this.gateway);
                    this.gatewayAccountDowntimes = this.gatewayAccountDowntimes.filter(downtime => downtime.id !== id);
                    this.$toast.positive('Downtime schedule successfully deleted.');
                } catch (error) {
                    this.$toast.negative('Downtime schedule could not be deleted.');
                }
            },
            async saveGatewayAccountDowntime(gatewayAccountDowntime) {
                const params = {
                    id: this.gateway.id,
                    data: gatewayAccountDowntime.getPayload(),
                };
                try {
                    const {fields} = await this.$api(({gatewayAccounts}) => gatewayAccounts.createDowntimeSchedule(params));
                    await this.notifyGatewayUpdate(this.gateway);
                    this.gatewayAccountDowntimes.push(new GatewayAccountDowntimeModel(fields));
                    this.$toast.positive('Downtime schedule successfully created.');
                } catch (error) {
                    this.$toast.negative('Downtime schedule could not be created.');
                }
            },
            async updateGatewayAccountDowntime(gatewayAccountDowntime) {
                const params = {
                    id: this.gateway.id,
                    downtimeScheduleId: gatewayAccountDowntime.id,
                    data: gatewayAccountDowntime.getPayload(),
                };
                try {
                    const {fields} = await this.$api(({gatewayAccounts}) => gatewayAccounts.updateDowntimeSchedule(params));
                    await this.notifyGatewayUpdate(this.gateway);
                    const updatedDowntime = new GatewayAccountDowntimeModel(fields);
                    this.gatewayAccountDowntimes = this.gatewayAccountDowntimes
                        .map(downtime => (downtime.id === updatedDowntime.id ? updatedDowntime : downtime));
                    this.$toast.positive('Downtime schedule successfully updated.');
                } catch (error) {
                    this.$toast.negative('Downtime schedule could not be updated.');
                }
            },
            /**
             * Refresh gateway every time a downtime is changed
             * in order to notify the current status of the gateway
             * @param {GatewayAccountModel} gatewayAccount
             * */
            async notifyGatewayUpdate({id}) {
                const {fields} = await this.$api(({gatewayAccounts}) => gatewayAccounts.get({id}));
                this.$emit('update-is-down', {isDown: fields.isDown});
            },
            async fetchData() {
                const params = {
                    id: this.gateway.id,
                    filter: 'status:ongoing,pending',
                };
                const {items} = await this.$api(({gatewayAccounts}) => gatewayAccounts.getAllDowntimeSchedules(params));
                this.gatewayAccountDowntimes = items.map(({fields}) => new GatewayAccountDowntimeModel(fields));
            },
        },
    };
</script>
